import { AdminApi } from 'api';
import useApiCall from 'hooks/use-api-call';
import React, { FunctionComponent, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalFooter, ModalContent, ModalFooterButtons } from 'ui/molecules/modal';
import { StateValues } from 'react-use-form-state';
import * as Styled from './styled';

export interface EditCeoFields {
  id: string;
  forename: string;
  surname: string;
  email: string;
}

export interface EditCeoModalProps {
  onHideEditCeo: () => void;
  fetchIssuerDetails: () => void;
  showSuccessModal: () => void;
  className?: string;
  values: EditCeoFields;
}

const EditCeoForm = makeModalForm<EditCeoFields>();

const EditCeoModal: FunctionComponent<EditCeoModalProps> = (props) => {
  const { onHideEditCeo, fetchIssuerDetails, showSuccessModal = () => {}, values } = props;

  const [issuerData, setIssuerData] = useState<EditCeoFields>(values);

  const { makeAuthenticatedApi, withApi, loading } = useApiCall();

  const adminApi: AdminApi = useMemo(() => makeAuthenticatedApi(AdminApi), [makeAuthenticatedApi]);

  const editCeo = (fieldsValues: StateValues<EditCeoFields>) => {
    withApi(async () => {
      const patchBody = {
        id: values.id,
        patchedAdminIssuerUpdateRequest: {
          naturalPerson: {
            ...fieldsValues,
          },
        },
      };
      await adminApi.adminIssuersPartialUpdate(patchBody);
      showSuccessModal();
      fetchIssuerDetails();
      onHideEditCeo();
    });
  };

  return (
    <>
      <Modal onClose={onHideEditCeo}>
        <EditCeoForm
          initial={{
            forename: issuerData.forename,
            surname: issuerData.surname,
            email: issuerData.email,
          }}
          onChange={(values: StateValues<EditCeoFields>) => {
            setIssuerData(values);
          }}
          onSubmit={(values: StateValues<EditCeoFields>) => {
            return editCeo(values);
          }}
          i18nKey="dashboardIssuerInvestorDetails.addIdentificationForm"
        >
          <ModalHeader>
            <Translate name="dashboardIssuerIssuerDetails.editCeoForm.title" />
          </ModalHeader>
          <ModalContent>
            <Styled.Row>
              <Styled.LeftColumn>
                <EditCeoForm.Group name="forename" required={!!values.forename}>
                  <EditCeoForm.Input value={issuerData.forename} placeholder="-" />
                </EditCeoForm.Group>
              </Styled.LeftColumn>
              <Styled.RightColumn>
                <EditCeoForm.Group name="surname" required={!!values.surname}>
                  <EditCeoForm.Input value={issuerData.surname} placeholder="-" />
                </EditCeoForm.Group>
              </Styled.RightColumn>
            </Styled.Row>
            <EditCeoForm.Group name="email" required={!!values.email}>
              <EditCeoForm.Input value={issuerData.email} placeholder="-" />
            </EditCeoForm.Group>
          </ModalContent>
          <ModalFooter>
            <ModalFooterButtons
              actionButtons={[
                {
                  name: 'cancel',
                  content: <Translate name="common.cancel" />,
                  size: 'large',
                  onClick: onHideEditCeo,
                },
                {
                  name: 'send',
                  content: <Translate name="common.saveChanges" />,
                  variant: 'primary',
                  size: 'large',
                  type: 'submit',
                  loading,
                },
              ]}
            />
          </ModalFooter>
        </EditCeoForm>
      </Modal>
    </>
  );
};

export default EditCeoModal;
